import { Menu, Portal } from '@chakra-ui/react'
import cn from 'clsx'
import { FC, useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Typography } from '@/components/ui'
import MovingStripes from '@/components/ui/animations/MovingStripes'

import Basket from '@/assets/icons/basket2.svg'
import CancelGray from '@/assets/icons/cancel-gray.svg'

import { PLATFROMS_LANGUAGES } from '@/constants/languages'
import { DraftsContext } from '@/features/Home/components/shotlistUpload/DraftsContext'
import { selectToFollowByTaskId } from '@/store/features/formUploader/formUploaderSelectors'
import { setToFollow as setToFollowAction } from '@/store/features/formUploader/formUploaderSlice'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { IOption } from '@/types/selectOption.interface'
import { ILanguage } from '@/types/task.interface'
import { ISelectedPlatform, TPlatformValue } from '@/types/uploadForm.interface'

interface IFormHeaderStatusProps {
	platformState?: ISelectedPlatform
	isUploading: boolean
	sourceName?: string
	transcribingLabel?: string
	taskId: string
	onCreate: (value: string) => void
	handleUploadSrt: (value: File) => void
	selectedPlatform: TPlatformValue | null
	availableSourceLanguages?: IOption[]
	selectedTranscribingLang?: ILanguage | null
	handleCloseErrorMessage?: () => void
	handleDeleteTranslation?: () => void
	onStopTranscription?: () => void
	selectedPlatformList: ISelectedPlatform[]
	isProcessing: boolean
	isTranscribing: boolean
}

const FormHeaderStatus: FC<IFormHeaderStatusProps> = ({
	platformState,
	isUploading,
	sourceName,
	transcribingLabel,
	taskId,
	onCreate,
	handleUploadSrt,
	selectedPlatform,
	availableSourceLanguages,
	selectedTranscribingLang,
	handleCloseErrorMessage,
	handleDeleteTranslation,
	onStopTranscription,
	selectedPlatformList,
	isProcessing,
	isTranscribing
}) => {
	const navigate = useNavigate()
	const { removeDraft } = useContext(DraftsContext)

	const dispatch = useAppDispatch()

	const toFollow = useAppSelector(selectToFollowByTaskId(taskId))

	const setToFollow = (value: boolean) => {
		dispatch(setToFollowAction({ taskId, value }))
	}

	const headerState = platformState?.headerState || 'idle'
	const processing = platformState?.processing
	const result = platformState?.result

	const platformLanguage = PLATFROMS_LANGUAGES[selectedPlatform]

	const buttonsDisabled =
		toFollow || isProcessing || isTranscribing || isUploading

	const isMainPlatform = selectedPlatformList?.[0]?.value === selectedPlatform

	const handleStopTranscription = () => {
		if (headerState === 'processing' && processing === 'transcribe')
			onStopTranscription()
	}

	const handleOpenShotlist = () => {
		if (!taskId) return

		removeDraft(taskId)
		navigate(`/file/${taskId}`)
	}

	// 1) PROCESSING
	if (headerState === 'processing') {
		if (processing === 'transcribe' && isUploading) {
			return (
				<div className='flex justify-between items-center bg-yellow-50 py-[22px] px-4 rounded-[12px]'>
					<Typography
						variant='text'
						className='text-[16px] !font-[500] !leading-[140%]'
					>
						Transcription starts after upload completes
					</Typography>
					<Button>
						<Basket />
					</Button>
				</div>
			)
		}

		if (processing === 'transcribe') {
			return (
				<MovingStripes
					bgColor='yellow-50'
					text={`Transcribing ${transcribingLabel || sourceName || ''}...`}
					handleStopTranscription={handleStopTranscription}
				/>
			)
		}

		if (processing === 'translate') {
			return (
				<MovingStripes
					bgColor='blue-50'
					text={`Translating to ${platformLanguage}...`}
					// Backend cannot interrupt translation process. So the button is hidden during translation
					hideCancelButton
				/>
			)
		}
	}

	// 2) DONE
	if (headerState === 'done') {
		const finalText =
			result === 'transcription'
				? `${platformLanguage} Transcription`
				: `${platformLanguage} Translation`

		return (
			<div className='flex justify-between items-center bg-green-50 py-[22px] px-4 rounded-[12px]'>
				<Typography
					variant='text'
					className='text-[16px] !font-[500] !leading-[140%]'
				>
					{finalText}
				</Typography>
				<div className='flex gap-2'>
					<Button
						className='bg-green-100 px-3'
						classNameText='text-black'
						onClick={handleOpenShotlist}
						disabled={!taskId || isProcessing}
					>
						Open
					</Button>
					<Button onClick={handleDeleteTranslation} disabled={isProcessing}>
						<Basket />
					</Button>
				</div>
			</div>
		)
	}

	// 3) ERROR
	if (headerState === 'error') {
		return (
			<div className='flex justify-between items-center bg-red-50 py-[22px] px-4 rounded-[12px]'>
				<Typography
					variant='text'
					className='text-[16px] !font-[500] !leading-[140%] text-red-600'
				>
					{'Unable to complete the request. Close this message and try again'}
				</Typography>

				<Button onClick={handleCloseErrorMessage}>
					<CancelGray />
				</Button>
			</div>
		)
	}

	// 4) IDLE (default)
	return (
		<div className='flex flex-col gap-[34px]'>
			<div className='flex gap-3'>
				<div className='flex'>
					<Button className='!p-0' disabled={buttonsDisabled}>
						<label
							htmlFor='srt-upload'
							className='px-[50.5px] py-[14px] rounded-[8px] bg-gray-600 text-white hover:bg-black z-0 cursor-pointer'
						>
							Upload SRT file
						</label>
						<input
							id='srt-upload'
							type='file'
							name='file'
							accept='.srt,.vtt,.ass,.ssa,.sbv,.sub,.lrc,.ttml,.dfxp,.scc,text/vtt,application/x-subrip,text/plain'
							className='hidden'
							multiple={false}
							disabled={buttonsDisabled}
							onChange={e => {
								const file = e.target.files?.[0]
								if (file) handleUploadSrt(file)
							}}
						/>
					</Button>
				</div>

				{availableSourceLanguages?.length > 1 ? (
					<Menu.Root positioning={{ placement: 'bottom-start' }}>
						<Menu.Trigger asChild>
							<Button
								disabled={buttonsDisabled}
								className='px-[52px] py-[14px] rounded-[8px] bg-gray-100 hover:bg-gray-200'
								classNameText='text-gray-600'
							>
								Create translation
							</Button>
						</Menu.Trigger>

						<Portal>
							<Menu.Positioner className='ml-4'>
								<Menu.Content>
									{availableSourceLanguages?.map(({ value, label }) => (
										<Menu.Item
											key={value}
											value={value}
											onClick={() => {
												if (label === platformLanguage) return
												onCreate(value)
											}}
										>
											{label}
										</Menu.Item>
									))}
								</Menu.Content>
							</Menu.Positioner>
						</Portal>
					</Menu.Root>
				) : (
					<Button
						className='px-[52px] py-[14px] rounded-[8px] bg-gray-100 hover:bg-gray-200'
						classNameText='text-gray-600'
						disabled={buttonsDisabled}
						onClick={() =>
							onCreate(
								selectedTranscribingLang?.code ||
									availableSourceLanguages[0]?.value
							)
						}
					>
						Create translation
					</Button>
				)}
			</div>

			<label
				className={cn(
					'group flex items-center gap-3 w-fit cursor-pointer select-none',
					!isMainPlatform && 'opacity-30 !cursor-default'
				)}
			>
				<input
					type='checkbox'
					checked={toFollow}
					onChange={e => setToFollow(e.target.checked)}
					className='sr-only'
					disabled={!isMainPlatform}
				/>

				<span
					className='relative flex h-5 w-5 items-center justify-center border-[3px] border-[#767676] bg-white
									group-has-[:checked]:border-[#333333]
									group-has-[:checked]:bg-[#333333]'
				>
					<span
						className='absolute top-[-1px] h-[12px] w-[8px] rotate-45 border-b-[3px] border-r-[3px] border-white
							opacity-0 group-has-[:checked]:opacity-100'
					/>
				</span>

				<span className='text-[18px] leading-[28px] text-black'>To follow</span>
			</label>
		</div>
	)
}

export default FormHeaderStatus
